import React, { useEffect, useState } from "react";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { fetchDistricts, fetchRegions } from "../services/locationService";

const formatHelper = (message, fallback) => message || fallback;

const LocationSelector = ({
  region,
  district,
  onChange,
  errors = {},
  disabled = false,
  required = true,
}) => {
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [regionFetchError, setRegionFetchError] = useState("");
  const [districtFetchError, setDistrictFetchError] = useState("");

  useEffect(() => {
    let active = true;
    setLoadingRegions(true);
    setRegionFetchError("");
    fetchRegions()
      .then((data) => {
        if (active) setRegions(data);
      })
      .catch((error) => {
        if (active) setRegionFetchError(error.message);
      })
      .finally(() => active && setLoadingRegions(false));

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    if (!region) {
      setDistricts([]);
      setDistrictFetchError("");
      return () => {
        active = false;
      };
    }

    setLoadingDistricts(true);
    setDistrictFetchError("");
    fetchDistricts(region)
      .then((data) => {
        if (active) setDistricts(data);
      })
      .catch((error) => {
        if (active) setDistrictFetchError(error.message);
      })
      .finally(() => active && setLoadingDistricts(false));

    return () => {
      active = false;
    };
  }, [region]);

  const handleRegionChange = (event) => {
    const value = event.target.value;
    onChange?.({ region: value, district: "" });
  };

  const handleDistrictChange = (event) => {
    const value = event.target.value;
    onChange?.({ region, district: value });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          required={required}
          disabled={disabled}
          error={!!(errors.region || regionFetchError)}
        >
          <InputLabel>Mkoa (Region)</InputLabel>
          <Select
            label="Mkoa (Region)"
            value={region || ""}
            onChange={handleRegionChange}
          >
            {loadingRegions && (
              <MenuItem disabled value="">
                Loading regions...
              </MenuItem>
            )}
            {!loadingRegions &&
              regions.map((item) => (
                <MenuItem key={item.name} value={item.name}>
                  {item.name}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>
            {formatHelper(
              errors.region || regionFetchError,
              "Mkoa is used for reports and profiling."
            )}
          </FormHelperText>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl
          fullWidth
          required={required}
          disabled={disabled || !region || loadingRegions}
          error={!!(errors.district || districtFetchError)}
        >
          <InputLabel>Wilaya (District)</InputLabel>
          <Select
            label="Wilaya (District)"
            value={district || ""}
            onChange={handleDistrictChange}
          >
            {!region && (
              <MenuItem disabled value="">
                Select Mkoa first
              </MenuItem>
            )}
            {region && loadingDistricts && (
              <MenuItem disabled value="">
                Loading districts...
              </MenuItem>
            )}
            {region &&
              !loadingDistricts &&
              districts.map((item) => (
                <MenuItem key={item.name} value={item.name}>
                  {item.name}
                </MenuItem>
              ))}
          </Select>
          <FormHelperText>
            {formatHelper(
              errors.district || districtFetchError,
              "Wilaya is required for group/member registration."
            )}
          </FormHelperText>
        </FormControl>
      </Grid>
    </Grid>
  );
};

export default LocationSelector;
